import React from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { ExternalLink, ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { buildAdminHotelAnalyticsUrl } from "../../utils/hotelAnalyticsNavigation";
import type {
  AdminHotelServicesRow,
  AdminServicePreview,
} from "../../utils/adminServicesMappers";

type AdminServicesTopByHotelTableProps = {
  rows: AdminHotelServicesRow[];
  loading: boolean;
  emptyMessage: string;
};

const MAX_TOP_SERVICES = 5;
const HEADER_CLASS =
  "px-2 py-3 text-center align-middle text-[10px] font-medium text-[#737373]";
const ROW_CLASS = "h-[74px] border-b border-[#E5E5E5] last:border-b-0";

function ServiceCell({ service }: { service?: AdminServicePreview }) {
  const [hasImageError, setHasImageError] = React.useState(false);

  if (!service) {
    return (
      <div className="mx-auto flex h-[56px] w-full max-w-[128px] items-center justify-center">
        <span className="text-[10px] text-zinc-400">—</span>
      </div>
    );
  }

  return (
    <div className="mx-auto flex h-[56px] w-full max-w-[128px] flex-col items-center justify-start gap-1">
      {service.imageUrl && !hasImageError ? (
        <img
          src={service.imageUrl}
          alt=""
          className="size-10 rounded-lg border border-[#E5E5E5] object-cover"
          onError={() => setHasImageError(true)}
        />
      ) : (
        <span className="flex size-10 items-center justify-center rounded-lg border border-[#E5E5E5] bg-[#FAFAFA] text-[#A3A3A3]">
          <ImageIcon className="size-4" strokeWidth={1.5} />
        </span>
      )}
      <span className="line-clamp-2 min-h-[20px] text-center text-[10px] leading-[1.05] text-[#0A0A0A]">
        {service.name}
      </span>
    </div>
  );
}

function LoadingRow({ index }: { index: number }) {
  return (
    <tr key={`loading-row-${index}`} className={ROW_CLASS}>
      <td className="px-2 py-3 align-middle text-center">
        <div className="mx-auto h-4 w-28 animate-pulse rounded bg-muted" />
      </td>
      {Array.from({ length: MAX_TOP_SERVICES }, (_, cellIndex) => (
        <td
          key={`loading-cell-${index}-${cellIndex}`}
          className="px-2 py-2 align-middle"
        >
          <div className="mx-auto flex h-[56px] w-full max-w-[128px] flex-col items-center gap-1">
            <div className="size-10 animate-pulse rounded-lg bg-muted" />
            <div className="h-3 w-20 animate-pulse rounded bg-muted" />
          </div>
        </td>
      ))}
      <td className="px-2 py-3 align-middle text-center">
        <div className="mx-auto size-8 animate-pulse rounded bg-muted" />
      </td>
    </tr>
  );
}

export const AdminServicesTopByHotelTable = ({
  rows,
  loading,
  emptyMessage,
}: AdminServicesTopByHotelTableProps): React.JSX.Element => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  return (
    <div className="bg-white p-4">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[960px] table-fixed border-collapse">
          <thead>
            <tr className="border-b border-[#E5E5E5]">
              <th className={HEADER_CLASS}>
                {t("analytics.admin.table.hotelBrand", "Hotel Brand")}
              </th>
              {Array.from({ length: MAX_TOP_SERVICES }, (_, index) => (
                <th
                  key={`top-service-header-${index + 1}`}
                  className={HEADER_CLASS}
                >
                  {t(
                    "analytics.admin.services.topServiceColumn",
                    "Top {{position}} service",
                    {
                      position: index + 1,
                    },
                  )}
                </th>
              ))}
              <th className={HEADER_CLASS}>
                {t("billing.guesteriaPartnersRevenue.actions", "Actions")}
              </th>
            </tr>
          </thead>
          <tbody>
            {loading
              ? Array.from({ length: 3 }, (_, index) => (
                  <LoadingRow key={`loading-${index}`} index={index} />
                ))
              : null}
            {!loading && rows.length === 0 ? (
              <tr>
                <td
                  colSpan={MAX_TOP_SERVICES + 2}
                  className="px-2 py-8 text-center text-sm text-zinc-500"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : null}
            {!loading
              ? rows.map((row) => (
                  <tr key={row.id} className={ROW_CLASS}>
                    <td className="px-2 py-3 align-middle text-center text-[10px] leading-tight text-[#0A0A0A]">
                      {row.hotelBrand}
                    </td>
                    {Array.from({ length: MAX_TOP_SERVICES }, (_, index) => (
                      <td
                        key={`${row.id}-top-${index + 1}`}
                        className="px-2 py-2 align-middle"
                      >
                        <ServiceCell service={row.topServices[index]} />
                      </td>
                    ))}
                    <td className="px-2 py-3 align-middle text-center">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        aria-label={t(
                          "billing.guesteriaPartnersRevenue.actions",
                          "Actions",
                        )}
                        onClick={() => {
                          const targetUrl = buildAdminHotelAnalyticsUrl(
                            "services",
                            searchParams,
                            row.id,
                          );
                          window.open(
                            targetUrl,
                            "_blank",
                            "noopener,noreferrer",
                          );
                        }}
                      >
                        <ExternalLink className="size-4 text-zinc-500" />
                      </Button>
                    </td>
                  </tr>
                ))
              : null}
          </tbody>
        </table>
      </div>
    </div>
  );
};
